// Ambient particle layer — fixed full-viewport overlay that renders
// petals, leaves, snowflakes or sparks depending on the active Dojo theme.
// Particles are CSS-animated; React only owns the layout (count + delays).

import { useEffect, useState } from "react";
import { getStoredThemeId, getThemeById } from "@/lib/dojoThemes";

const PARTICLE_COUNT = {
  lasers: 6,
  petals: 18,
  leaves: 14,
  snow: 28,
  sparks: 20,
};

function buildParticles(ambience, count) {
  // Deterministic per-mount distribution: random but fresh on each remount.
  const items = [];
  for (let i = 0; i < count; i++) {
    const left = Math.random() * 100;       // 0..100 vw
    const delay = Math.random() * 12;       // up to 12s
    const duration = 8 + Math.random() * 10; // 8..18s
    const size = 0.7 + Math.random() * 0.7;  // 0.7..1.4 scale
    items.push({ id: i, left, delay, duration, size });
  }
  return items;
}

export default function DojoAmbience() {
  const [theme, setTheme] = useState(() => getThemeById(getStoredThemeId()));

  useEffect(() => {
    const handler = (e) => setTheme(e.detail);
    window.addEventListener("dojo-theme-changed", handler);
    return () => window.removeEventListener("dojo-theme-changed", handler);
  }, []);

  const ambience = theme.ambience || "lasers";
  const count = PARTICLE_COUNT[ambience] ?? 12;
  const PARTICLE_CLASS = {
    lasers: "laser",
    petals: "petal",
    leaves: "leaf",
    snow: "snow",
    sparks: "spark",
  };
  const cls = PARTICLE_CLASS[ambience] || "laser";
  // Rebuild particles when ambience changes so distribution looks fresh
  const particles = buildParticles(ambience, count);

  return (
    <div className="bc-ambience" aria-hidden="true">
      {particles.map((p) => (
        <span
          key={`${ambience}-${p.id}`}
          className={`bc-particle bc-particle--${cls}`}
          style={{
            left: `${p.left}vw`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            transform: `scale(${p.size})`,
          }}
        />
      ))}
    </div>
  );
}
